<template>
  <div v-loading="loading">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h4 class="m-0">
        {{ isNew ? 'New static page' : title }}
        <small v-if="!isNew" class="text-muted">#{{ docId }}</small>
      </h4>
      <el-button size="small" @click="backToList">Back to list</el-button>
    </div>

    <card class="mb-3">
      <div class="d-flex align-items-end" style="gap: 16px; flex-wrap: wrap;">
        <div style="flex: 1 1 320px;">
          <label>Slug <span class="required">*</span></label>
          <el-input v-model="form.alias" placeholder="e.g. medical-disclaimer" />
          <small class="text-muted">Used in the page URL. Must be unique.</small>
        </div>
        <div>
          <label>Visible on the site</label>
          <div>
            <el-switch v-model="form.isVisible" active-text="Visible" inactive-text="Hidden" />
          </div>
        </div>
        <div>
          <el-button type="primary"
                     :loading="savingDoc"
                     :disabled="!canSaveDoc"
                     @click="saveDoc">Save page</el-button>
        </div>
      </div>

      <el-alert v-if="aliasError"
                type="error"
                :closable="false"
                class="mt-16"
                :title="aliasError" />
    </card>

    <card>
      <el-tabs v-model="activeLanguage">
        <el-tab-pane v-for="lang in languages"
                     :key="lang"
                     :name="lang">
          <span slot="label">
            {{ lang.toUpperCase() }}
            <i v-if="hasContent(lang)" class="fa fa-circle filled-marker" />
          </span>

          <div class="mb-3">
            <label>Title ({{ lang.toUpperCase() }})</label>
            <el-input v-model="form.title[lang]" :placeholder="`Page title in ${lang.toUpperCase()}`" />
            <small class="text-muted">Saved together with the page, by "Save page" above.</small>
          </div>

          <div class="mb-3">
            <label>Body ({{ lang.toUpperCase() }})</label>
            <!-- Mounted only for the visible tab: CKEditor is heavy and there are ten languages. -->
            <ckeditor v-if="activeLanguage === lang"
                      v-model="body[lang]"
                      :config="editorConfig" />
          </div>

          <el-button type="primary"
                     :loading="savingText === lang"
                     :disabled="!canSaveText"
                     @click="saveText(lang)">Save {{ lang.toUpperCase() }} body</el-button>
        </el-tab-pane>
      </el-tabs>
    </card>
    <TextRevisions :target="revisionTarget" :busy="loading || savingDoc || Boolean(savingText)" />
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import TextRevisions from '@/components/Entities/TextRevisions.vue';

export default {
  name: 'DocEdit',
  components: { TextRevisions },
  data: () => ({
    loading: false,
    savingDoc: false,
    // holds the language currently being saved, so only that button spins
    savingText: '',
    activeLanguage: '',
    aliasError: '',
    form: {
      alias: '',
      isVisible: false,
      title: {},
    },
    body: {},
    editorConfig: {
      // The editor keeps the markup it is given. These pages were written in the legacy admin and
      // contain hand-made HTML, which a filtering editor would quietly strip on first save.
      allowedContent: true,
      // The stored body carries the public site's class names, which live in the site repo. Its
      // styles are copied into public/css/doc-content.css so the editor shows something close to
      // the finished page instead of unstyled blocks — see docs/STATIC_PAGE_STYLES.md.
      contentsCss: ['/css/doc-content.css'],
      // 132 of the 141 copied rules are scoped under .doc-page, the wrapper the site puts around
      // page content. The editable body stands in for that wrapper, so it needs the class.
      bodyClass: 'doc-page',
    },
  }),
  computed: {
    ...mapState('docs', ['docs', 'doc', 'docTexts', 'saveError']),
    ...mapState('cmsApiCatalogs', ['domains']),

    isNew() {
      return this.$route.params.id === 'new';
    },
    docId() {
      if (this.isNew) {
        return 0;
      }

      return Number(this.$route.params.id);
    },
    languages() {
      return this.domains.map((domain) => domain.language);
    },
    title() {
      const titles = this.form.title || {};

      return titles.en || titles.ru || this.form.alias || `#${this.docId}`;
    },
    canSaveDoc() {
      return this.form.alias.trim().length > 0;
    },
    /**
     * A body belongs to a page, so there has to be a page first: a brand-new one has no id to
     * attach the text to until it has been saved once.
     */
    canSaveText() {
      return this.docId > 0;
    },
    revisionTarget() {
      if (!this.docId || !this.activeLanguage) return null;
      return { kind: 'text_set', lang: this.activeLanguage, entity: 'docs', entity_id: this.docId };
    },
  },
  async created() {
    await this.load();
  },
  methods: {
    ...mapActions('docs', ['getDocs', 'getDoc', 'getDocTexts', 'upsertDoc', 'saveDocText', 'clearDoc']),
    ...mapActions('cmsApiCatalogs', ['getDomains']),

    async load() {
      this.loading = true;
      try {
        await this.clearDoc();

        if (!this.domains.length) {
          await this.getDomains();
        }
        this.activeLanguage = this.languages[0] || '';

        if (this.isNew) {
          this.form = { alias: '', isVisible: false, title: {} };
          this.body = {};

          return;
        }

        await this.loadExistingPage();
      } catch (error) {
        this.$notify({ type: 'warning', title: 'Could not load the page' });
      } finally {
        this.loading = false;
      }
    },

    /**
     * The page is read from the store the CMS writes into, and the catalogue row is the fallback:
     * three of the legacy pages have never been saved here, so they exist in the list only.
     */
    async loadExistingPage() {
      const [saved] = await Promise.all([
        this.getDoc(this.docId),
        this.getDocTexts(this.docId).catch(() => ({})),
      ]);

      if (!this.docs.length) {
        await this.getDocs().catch(() => {});
      }

      const source = saved || this.docs.find((doc) => doc.id === this.docId) || {};

      this.form = {
        alias: source.alias || '',
        isVisible: !source.is_hide,
        // Empty strings arrive for languages that were never filled; dropping them keeps the
        // inputs empty instead of showing a value that is not there.
        title: this.compactTitle(source.title),
      };
      this.body = { ...this.docTexts };
    },

    compactTitle(title) {
      const compacted = {};
      Object.keys(title || {}).forEach((lang) => {
        if (title[lang]) {
          compacted[lang] = title[lang];
        }
      });

      return compacted;
    },

    hasContent(lang) {
      return Boolean((this.body[lang] || '').trim());
    },

    async saveDoc() {
      this.savingDoc = true;
      this.aliasError = '';
      try {
        const saved = await this.upsertDoc({
          id: this.docId,
          alias: this.form.alias.trim(),
          title: this.form.title,
          isHide: !this.form.isVisible,
        });

        if (!saved) {
          // A refused save comes back in the body, not as a failed request. The reason belongs to
          // the service, so it is reported rather than guessed at.
          this.aliasError = this.saveError || 'The page could not be saved.';

          return;
        }

        this.$notify({ type: 'success', title: 'Page saved' });

        if (this.isNew && saved.id) {
          this.$router.replace({ name: 'StaticPage', params: { id: saved.id } });
        }
      } catch (error) {
        this.$notify({ type: 'warning', title: 'Could not save the page' });
      } finally {
        this.savingDoc = false;
      }
    },

    async saveText(lang) {
      this.savingText = lang;
      try {
        await this.saveDocText({
          docId: this.docId,
          lang,
          text: this.body[lang] || '',
        });

        this.$notify({ type: 'success', title: `${lang.toUpperCase()} body saved` });
      } catch (error) {
        this.$notify({ type: 'warning', title: `Could not save the ${lang.toUpperCase()} body` });
      } finally {
        this.savingText = '';
      }
    },

    backToList() {
      this.$router.push({ name: 'StaticPages' });
    },
  },
};
</script>

<style scoped>
.required {
  color: #a94442;
}

.filled-marker {
  font-size: 6px;
  vertical-align: middle;
  color: #2dce89;
}

.mt-16 {
  margin-top: 16px;
}
</style>
